'use client';

import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  heroDiscountCodes,
  getHeroDiscountPercentage,
} from 'components/shared/Cart/utils';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { highlightElement } from 'components/ring-air/utils/highlightElement';
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { ClueShopifyMap } from 'content/products/addons/clue';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import {
  RingProColors,
  RingProChargerCaseColor,
  RingSizes,
  RingProChargerCaseVariants,
  getRingProBasePrice,
  AUTO_PAIR_CHARGER_WITH_RING,
  RING_COLOR_TO_CHARGER_MAP,
} from 'content/products/ring-pro';
import { UhxShopifyMap } from 'content/products/uhx';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';
import {
  ringProReferralCodes,
  getCampaignPromoDiscountConfig,
} from 'content/referral';
import { getCampaignConfig } from 'content/campaigns';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  addonsReducerRingPro,
  getCheckoutLineItemsRingPro,
  initialAddonsStateRingPro,
} from './utils/checkout';
import type {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  AddonsAction,
  AddonsState,
  RingProContextType,
  ServerDiscountInfo,
  ShippingEstimateObject,
  TierCampaignData,
} from './utils/types';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import {
  getTradeInCredit,
  getMaxTradeInCredit,
} from 'content/products/tradeIn';
import { RingProSelectionContext } from './RingProSelectionContextValue';

export const RingProSelectionContextProvider: React.FC<{
  children?: React.ReactNode;
  currency: Currency;
  defaultColor: RingProColors;
  defaultSize: RingSizes | null;
  defaultUhx: boolean;
  serverDiscount: ServerDiscountInfo;
  estimates?: ShippingEstimateObject;
  notServicable?: boolean;
  notServicableMessage?: string;
  uhxFirstOnly?: boolean;
  isOpenSizeDisabled?: boolean;
  showEngravingSectionProp?: boolean;
  tierCampaign?: TierCampaignData | null;
  onCheckoutStarted?: () => void;
}> = ({
  children,
  currency,
  defaultColor,
  defaultSize,
  defaultUhx,
  serverDiscount,
  estimates, // eslint-disable-line @typescript-eslint/no-unused-vars
  notServicable,
  notServicableMessage,
  uhxFirstOnly,
  isOpenSizeDisabled,
  showEngravingSectionProp = true,
  tierCampaign = null,
  onCheckoutStarted,
}) => {
  const _t = useTranslations('ring');
  const tRingPro = useTranslations('ring-pro');
  const searchParams = useSearchParams();
  const CartClient = useCartStore(
    useShallow((s) => ({
      addToCart: s.addToCart,
      workAffiliateDiscount: s.workAffiliateDiscount,
      affiliateDetails: s.affiliateDetails,
      getCampaignForProduct: s.getCampaignForProduct,
      store: s.store,
    })),
  );
  const {
    addToCart,
    workAffiliateDiscount,
    affiliateDetails,
    getCampaignForProduct,
  } = CartClient;
  const regionLocale = useRegionLocale();
  const country = searchParams?.get('locale') ?? regionLocale.region;
  const { isCnOPlus } = useIsCnOPlus();

  const ringProCampaign = getCampaignForProduct('ring-pro');
  const campaignConfig = getCampaignConfig(ringProCampaign);

  const showSizeRef = useRef<HTMLDivElement>(null);
  const showChargerRef = useRef<HTMLDivElement>(null);
  const engravingRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);
  const powerplugRef = useRef<HTMLDivElement>(null);
  const cartSummaryRef = useRef<HTMLDivElement>(null);
  const sectionRefs = {
    showSizeRef,
    showChargerRef,
    engravingRef,
    coverageRef,
    powerplugRef,
    cartSummaryRef,
  };

  const colorParam = searchParams?.get('color');
  const initialColor =
    colorParam && (Object.values(RingProColors) as string[]).includes(colorParam)
      ? (colorParam as RingProColors)
      : defaultColor;
  const [color, setColor] = useState<RingProColors>(initialColor);
  const [size, setSize] = useState<RingSizes | null>(
    isOpenSizeDisabled ? null : defaultSize ?? RingSizes.OPEN,
  );
  const [chargerCaseColor, setChargerCaseColor] =
    useState<RingProChargerCaseColor | null>(
      AUTO_PAIR_CHARGER_WITH_RING
        ? RING_COLOR_TO_CHARGER_MAP[initialColor]
        : RingProChargerCaseColor.GOLD,
    );
  const [uhx, setUhx] = useState<boolean>(defaultUhx);
  const [addonsState, dispatchAddons] = useReducer(
    addonsReducerRingPro,
    initialAddonsStateRingPro,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (AUTO_PAIR_CHARGER_WITH_RING) {
      setChargerCaseColor(RING_COLOR_TO_CHARGER_MAP[color]);
    }
  }, [color]);

  const store = CartClient.store;
  const {
    engraving,
    uhxCart,
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    clueCart,
    respiratoryCart,
    tradeIn,
  } = addonsState;

  const setEngraving = (value: boolean) =>
    dispatchAddons({ type: 'SET_ENGRAVING', payload: value });
  const setEngravingMessage = (value: string | null) =>
    dispatchAddons({ type: 'SET_ENGRAVING_MESSAGE', payload: value });
  const setUhxCart = (value: AddonsState['uhxCart']) =>
    dispatchAddons({ type: 'SET_UHX_CART', payload: value });
  const setAfibCart = (value: AddonsState['afibCart']) =>
    dispatchAddons({ type: 'SET_AFIB_CART', payload: value });
  const setCardioAdaptabilityCart = (
    value: AddonsState['cardioAdaptibilityCart'],
  ) => dispatchAddons({ type: 'SET_CARDIO_ADAPTIBILITY_CART', payload: value });
  const setCnoProCart = (value: AddonsState['cnoProCart']) =>
    dispatchAddons({ type: 'SET_CNO_PRO_CART', payload: value });
  const setClueCart = (value: AddonsState['clueCart']) =>
    dispatchAddons({ type: 'SET_CLUE_CART', payload: value });
  const setRespiratoryCart = (value: AddonsState['respiratoryCart']) =>
    dispatchAddons({ type: 'SET_RESPIRATORY_CART', payload: value });
  const setTradeInDevice = (value: AddonsState['tradeIn']['device']) =>
    dispatchAddons({ type: 'SET_TRADE_IN_DEVICE', payload: value });
  const setTradeInSerialNumber = (value: string) =>
    dispatchAddons({ type: 'SET_TRADE_IN_SERIAL_NUMBER', payload: value });

  const hasUhx = Boolean(uhx || uhxCart);
  const tradeInCredit = useMemo(() => {
    if (!tradeIn.device || !store) return 0;
    return getTradeInCredit(tradeIn.device, store, hasUhx);
  }, [tradeIn.device, store, hasUhx]);
  const tradeInMaxCredit = useMemo(() => {
    if (!store) return 0;
    return getMaxTradeInCredit(store, true);
  }, [store]);

  const currencySymbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const basePrice = useMemo(() => {
    if (!store) return 0;
    return getRingProBasePrice(store, currency) ?? 0;
  }, [store, currency]);

  const price = useMemo(() => {
    if (!store) return 0;
    if (serverDiscount.applicable && serverDiscount.discountedPrice) {
      return serverDiscount.discountedPrice;
    }
    return getRingProBasePrice(store, currency) ?? 0;
  }, [store, currency, serverDiscount]);

  const ultraHumanPrice = useMemo(
    () => (uhxCart && !uhx && store ? UhxShopifyMap[store][uhxCart].price : 0),
    [uhxCart, uhx, store],
  );

  const chargerPrice = useMemo(() => {
    if (!store || !chargerCaseColor || !size) return 0;
    return (
      RingProChargerCaseVariants[chargerCaseColor]?.[size]?.price[store] ?? 0
    );
  }, [store, chargerCaseColor, size]);

  const afibPrice = useMemo(
    () => (afibCart && store ? AfibShopifyMap[store][afibCart].price : 0),
    [afibCart, store],
  );
  const cardioAdaptibilityPrice = useMemo(
    () =>
      cardioAdaptibilityCart && store
        ? CardioAdaptibilityShopifyMap[store][cardioAdaptibilityCart].price
        : 0,
    [cardioAdaptibilityCart, store],
  );
  const cnoProPrice = useMemo(() => {
    if (!cnoProCart || !store) return 0;
    const cnoMap = isCnOPlus ? CnoPlusShopifyMap : CnoProShopifyMap;
    return cnoMap[store][cnoProCart].price;
  }, [cnoProCart, store, isCnOPlus]);
  const cluePrice = useMemo(
    () => (clueCart && store ? ClueShopifyMap[store]?.Year1?.price ?? 0 : 0),
    [clueCart, store],
  );
  const respiratoryPrice = useMemo(
    () =>
      respiratoryCart && store
        ? RespiratoryShopifyMap[store]?.Year1?.price ?? 0
        : 0,
    [respiratoryCart, store],
  );

  // --- Discount Calculations ---
  const referral =
    searchParams?.get('referral') && store
      ? ringProReferralCodes[store as ShopifyStore]
      : undefined;

  const workAffiliate =
    searchParams?.get('workAffiliate') && searchParams?.get('discount');

  const affiliateDiscount = useMemo(() => {
    if (searchParams?.get('affiliateCode') === 'birthday') return 0;
    if (workAffiliate) {
      return (price * (workAffiliateDiscount?.discount ?? 0)) / 100;
    }
    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (
      !effectiveStore ||
      !affiliateDetails ||
      // !affiliateDetails.ringActive ||
      referral
    )
      return 0;
    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.RING_PRO;
    if (!discountConfig?.code) return 0;
    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * price) / 100
      : discountConfig?.value ?? 0;
  }, [
    affiliateDetails,
    price,
    workAffiliate,
    workAffiliateDiscount,
    store,
    referral,
    searchParams,
  ]);

  const heroCategoryParam = searchParams?.get('heroCategory');
  const heroCategory =
    heroCategoryParam && heroDiscountCodes[heroCategoryParam]
      ? heroCategoryParam
      : null;
  const heroDiscount = heroCategory ? price * getHeroDiscountPercentage() : 0;

  const birthdayDiscountCode = searchParams?.get('discount') as string;
  const affiliateCode = searchParams?.get('affiliateCode') as string;
  let birthdayDiscountPercentage = 0;
  if (birthdayDiscountCode && affiliateCode === 'birthday') {
    const lastTwoChars = birthdayDiscountCode.slice(-2);
    const parsedPercentage = parseInt(lastTwoChars);
    if (
      !isNaN(parsedPercentage) &&
      parsedPercentage > 0 &&
      parsedPercentage <= 100
    ) {
      birthdayDiscountPercentage = parsedPercentage;
    } else {
      birthdayDiscountPercentage = 15;
    }
  }
  const birthdayDiscount =
    birthdayDiscountCode &&
    birthdayDiscountCode.length > 0 &&
    affiliateCode === 'birthday'
      ? price * (birthdayDiscountPercentage / 100)
      : 0;

  const ringPromoDiscountAmount = useMemo(() => {
    const promoConfig = getCampaignPromoDiscountConfig({
      store,
      country,
      product: 'ring-pro',
      campaignConfig,
    });
    if (!promoConfig?.active || !promoConfig?.discount) return 0;
    const discountValue = promoConfig.discount;
    const discountType = promoConfig.discountType;
    return discountType === 'percentage'
      ? price * (discountValue / 100)
      : discountValue;
  }, [store, country, price, campaignConfig]);

  const discounts = useMemo(
    () => [
      { type: 'referral', amount: referral ? price * 0.1 : 0 },
      { type: 'affiliate', amount: affiliateDiscount },
      { type: 'hero', amount: heroDiscount },
      { type: 'birthday', amount: birthdayDiscount },
      { type: 'ringPromo', amount: ringPromoDiscountAmount },
    ],
    [
      referral,
      affiliateDiscount,
      heroDiscount,
      birthdayDiscount,
      ringPromoDiscountAmount,
      price,
    ],
  );

  const priceDrop = useMemo(
    () => (basePrice > price ? basePrice - price : 0),
    [basePrice, price],
  );

  // Derive affiliate discount code for checkout
  const affiliateDiscountCode = useMemo(() => {
    if (workAffiliateDiscount?.discountCode)
      return workAffiliateDiscount.discountCode;
    const effectiveStore = getEffectiveStoreForDiscount(store);
    if (!effectiveStore || !affiliateDetails) return undefined;
    return affiliateDetails.discount_configs[effectiveStore]?.RING_PRO?.code;
  }, [workAffiliateDiscount, affiliateDetails, store]);

  const effectiveDiscount = useMemo(() => {
    const serverActive = serverDiscount.applicable && basePrice > price;
    const serverAmt = serverActive ? basePrice - price : 0;

    if (serverActive) {
      // Tier discount is active — check if affiliate or trade-in can stack
      if (tradeIn.device) {
        // Trade-in replaces affiliate; tier still applies
        return {
          type: 'server+tradeIn' as const,
          amount: serverAmt,
          serverAmount: serverAmt,
          affiliateAmount: 0,
          label: serverDiscount.label ?? 'Discount',
          priceTagText: serverDiscount.priceTagText,
          code: serverDiscount.code,
          affiliateCode: undefined,
        };
      }
      if (affiliateDiscount > 0) {
        // Tier + affiliate stack
        return {
          type: 'server+affiliate' as const,
          amount: serverAmt + affiliateDiscount,
          serverAmount: serverAmt,
          affiliateAmount: affiliateDiscount,
          label: serverDiscount.label ?? 'Discount',
          priceTagText: serverDiscount.priceTagText,
          code: serverDiscount.code,
          affiliateCode: affiliateDiscountCode,
        };
      }
      // Tier only
      return {
        type: 'server' as const,
        amount: serverAmt,
        serverAmount: serverAmt,
        affiliateAmount: 0,
        label: serverDiscount.label ?? 'Discount',
        priceTagText: serverDiscount.priceTagText,
        code: serverDiscount.code,
        affiliateCode: undefined,
      };
    }

    // No server discount — original logic
    if (tradeIn.device) {
      return {
        type: 'tradeIn' as const,
        amount: 0,
        serverAmount: 0,
        affiliateAmount: 0,
        label: undefined,
        priceTagText: undefined,
        code: undefined,
        affiliateCode: undefined,
      };
    }
    // Pick the highest discount
    const best = discounts.reduce(
      (max, current) => (current.amount > max.amount ? current : max),
      { type: 'referral', amount: 0 },
    );
    return {
      ...best,
      serverAmount: 0,
      affiliateAmount: 0,
      label: undefined,
      priceTagText: undefined,
      code: undefined,
      affiliateCode: undefined,
    };
  }, [
    serverDiscount,
    basePrice,
    price,
    discounts,
    tradeIn.device,
    affiliateDiscount,
    affiliateDiscountCode,
  ]);

  const campaignActive = campaignConfig?.active ?? false;

  const effectivePrice = useMemo(
    () =>
      price +
      chargerPrice +
      cardioAdaptibilityPrice +
      afibPrice +
      cnoProPrice +
      cluePrice +
      respiratoryPrice +
      ultraHumanPrice -
      effectiveDiscount.affiliateAmount -
      (effectiveDiscount.serverAmount > 0 ? 0 : effectiveDiscount.amount),
    [
      price,
      chargerPrice,
      cardioAdaptibilityPrice,
      afibPrice,
      cnoProPrice,
      cluePrice,
      respiratoryPrice,
      ultraHumanPrice,
      effectiveDiscount,
    ],
  );

  const shippingEstimate = useMemo(
    () => (
      <span>
        {tRingPro.rich('ring-pro.selectionContext.shippingEstimate', {
          date: tRingPro('ring-pro.selectionContext.shippingDate'),
          strong: (chunks) => <strong>{chunks}</strong>,
        })}
      </span>
    ),
    [tRingPro],
  );

  // Disable UHX when affiliate is present
  useEffect(() => {
    if (searchParams?.get('affiliate') || searchParams?.get('affiliateCode')) {
      setUhxCart(null);
    }
  }, [searchParams]);
  const showEngravingSection = showEngravingSectionProp ?? true;

  const goToNextStep = () => {
    if (!size) highlightElement(showSizeRef);
    else if (!chargerCaseColor) highlightElement(showChargerRef);
    else if (engraving.enabled && !engraving.message)
      highlightElement(engravingRef);
    else if (!uhx && uhxCart === null) highlightElement(coverageRef);
  };

  const getExpressCheckoutLineItems = () => {
    if (!color || !size || !store || !chargerCaseColor || notServicable) {
      return [];
    }
    return getCheckoutLineItemsRingPro({
      color,
      size,
      store: store as ShopifyStore,
      chargerCaseColor,
      engraving: engraving.enabled,
      engravingMessage: engraving.message,
      uhxCart: uhxCart ?? null,
      afibCart: afibCart ?? null,
      cardioAdaptibilityCart: cardioAdaptibilityCart ?? null,
      cnoProCart: cnoProCart ?? null,
      clueCart: clueCart ?? null,
      respiratoryCart: respiratoryCart ?? null,
      serverDiscount,
      affiliateDiscountCode: effectiveDiscount.affiliateCode,
      isCnOPlus,
      tradeInDevice: tradeIn.device,
      tradeInSerialNumber: tradeIn.serialNumber,
    });
  };

  const checkout = async () => {
    onCheckoutStarted?.();
    if (!color || !size || !store || !chargerCaseColor || notServicable) return;
    try {
      setIsLoading(true);
      const lineItems = getCheckoutLineItemsRingPro({
        color,
        size,
        store: store as ShopifyStore,
        chargerCaseColor,
        engraving: engraving.enabled,
        engravingMessage: engraving.message,
        uhxCart: uhxCart ?? null,
        afibCart: afibCart ?? null,
        cardioAdaptibilityCart: cardioAdaptibilityCart ?? null,
        cnoProCart: cnoProCart ?? null,
        clueCart: clueCart ?? null,
        respiratoryCart: respiratoryCart ?? null,
        serverDiscount,
        affiliateDiscountCode: effectiveDiscount.affiliateCode,
        isCnOPlus,
        tradeInDevice: tradeIn.device,
        tradeInSerialNumber: tradeIn.serialNumber,
      });
      await addToCart(lineItems, false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: RingProContextType = useMemo(
    () => ({
      color,
      setColor,
      size,
      setSize,
      chargerCaseColor,
      setChargerCaseColor,
      defaultCurrency: currency,
      currencySymbol,
      uhx,
      setUhx,
      uhxCart: uhxCart ?? null,
      setUhxCart,
      afibCart: afibCart ?? null,
      setAfibCart,
      cardioAdaptibilityCart: cardioAdaptibilityCart ?? null,
      setCardioAdaptabilityCart,
      cnoProCart: cnoProCart ?? null,
      setCnoProCart,
      clueCart: clueCart ?? null,
      setClueCart,
      respiratoryCart: respiratoryCart ?? null,
      setRespiratoryCart,
      engraving: engraving.enabled,
      setEngraving,
      engravingMessage: engraving.message,
      setEngravingMessage,
      checkout,
      getExpressCheckoutLineItems,
      isLoading,
      store,
      serverDiscount,
      basePrice,
      price,
      ultraHumanPrice,
      effectivePrice,
      chargerPrice,
      shippingEstimate,
      affiliateDetails,
      campaignActive,
      affiliateDiscount,
      referral,
      showEngravingSection,
      priceDrop,
      effectiveDiscount,
      sectionRefs,
      goToNextStep,
      notServicable,
      notServicableMessage,
      uhxFirstOnly,
      isOpenSizeDisabled,
      tradeInDevice: tradeIn.device,
      setTradeInDevice,
      tradeInSerialNumber: tradeIn.serialNumber,
      setTradeInSerialNumber,
      tradeInCredit,
      tradeInMaxCredit,
      hasUhx,
      tierCampaign,
    }),
    [
      color,
      size,
      chargerCaseColor,
      currency,
      currencySymbol,
      uhx,
      uhxCart,
      afibCart,
      cardioAdaptibilityCart,
      cnoProCart,
      clueCart,
      respiratoryCart,
      engraving.enabled,
      engraving.message,
      isLoading,
      store,
      serverDiscount,
      basePrice,
      price,
      ultraHumanPrice,
      effectivePrice,
      chargerPrice,
      shippingEstimate,
      affiliateDetails,
      campaignActive,
      affiliateDiscount,
      referral,
      showEngravingSection,
      priceDrop,
      effectiveDiscount,
      sectionRefs,
      notServicable,
      notServicableMessage,
      uhxFirstOnly,
      isOpenSizeDisabled,
      tradeIn.device,
      tradeIn.serialNumber,
      tradeInCredit,
      tradeInMaxCredit,
      hasUhx,
      tierCampaign,
    ],
  );

  return (
    <RingProSelectionContext.Provider value={value}>
      {children}
    </RingProSelectionContext.Provider>
  );
};
